import { z } from "zod";

/**
 * Y5 BILLING (decision row 271; CLAUDE.md sections 0.5, 3 and 4). What a client's plan IS in this
 * system: a tier, a seat count, and which premium features are on. Nothing here is a price.
 *
 * NO FIGURE LIVES IN CODE. What a plan costs lives in Stripe and, where a customer is told, in the KB
 * (section 4: a figure must exact-match a KB source). `billing.test.ts` greps this file for a currency
 * figure and fails on a hit. These flags switch FEATURES; they never gate a price and they never change
 * what one customer is quoted (section 3: no price or quote differentiated by who the buyer is).
 *
 * The vendor side (a Stripe customer, a subscription) is the gateway tool `billing.customer.ensure` /
 * `billing.subscription.read` (packages/gateway/src/stripeTool.ts), a stub until Kabir's go.
 *
 * The state is human-set only: `pnpm billing set-plan|set-seats|set-flags`, run by a person. No agent
 * writes it (section 3: caps and plan limits are T3), and `billing_accounts.updated_by` refuses an
 * agent id at the database as well (migration 0057).
 */

/**
 * The plan tiers. AWAITING RATIFICATION (Kabir): the names. `private_build` is every client today (one
 * process per client, the founding clients); `multi_tenant` is the productized shape wave Y prepares.
 */
export const PLAN_TIERS = ["private_build", "multi_tenant"] as const;
export type PlanTier = (typeof PLAN_TIERS)[number];

/**
 * The premium flags. The first three are the automations that START a conversation rather than answer
 * one (they stood in as a hard-coded set in the web's automations module before this existed); the
 * fourth lifts the analyst's free asks per week. AWAITING RATIFICATION (Kabir): which features are
 * premium at all, and the names.
 */
export const PREMIUM_FLAGS = ["ghost_revival", "post_purchase_care", "review_ask", "analyst_unlimited"] as const;
export type PremiumFlag = (typeof PREMIUM_FLAGS)[number];

/** The seat count's bounds, mirrored by a CHECK in migration 0057. */
export const SEATS_MIN = 1;
export const SEATS_MAX = 500;

/**
 * Seats a client has when no `billing_accounts` row exists. AWAITING RATIFICATION (Kabir). It is the
 * ceiling, not a recommendation: today nothing limits a client's members, and a default below the
 * ceiling would refuse an invite a client can make now. A person sets a real count with
 * `pnpm billing set-seats`.
 */
export const DEFAULT_SEATS = SEATS_MAX;

/**
 * The flags on when no row exists: the three a client has today, so nothing switches off. The analyst
 * stays at its free asks per week. AWAITING RATIFICATION (Kabir): each default.
 */
export const DEFAULT_PREMIUM_FLAGS: readonly PremiumFlag[] = Object.freeze(["ghost_revival", "post_purchase_care", "review_ask"] as const);

export const isPlanTier = (v: unknown): v is PlanTier => typeof v === "string" && (PLAN_TIERS as readonly string[]).includes(v);
export const isPremiumFlag = (v: unknown): v is PremiumFlag => typeof v === "string" && (PREMIUM_FLAGS as readonly string[]).includes(v);

export const BillingState = z.strictObject({
  plan: z.enum(PLAN_TIERS),
  seats: z.number().int().min(SEATS_MIN).max(SEATS_MAX),
  premiumFlags: z
    .array(z.enum(PREMIUM_FLAGS))
    .refine((a) => new Set(a).size === a.length, "premiumFlags holds a flag twice"),
  stripeCustomerRef: z.string().min(1).nullable(),
  /** ISO time of the last change, or null when no row exists and these are the defaults. */
  updatedAt: z.iso.datetime().nullable(),
  /** Who set it (`user:<id>` or `cli:<name>`), or null when no row exists. */
  updatedBy: z.string().min(1).nullable(),
});
export type BillingState = z.infer<typeof BillingState>;

export const DEFAULT_BILLING_STATE: BillingState = Object.freeze({
  plan: "private_build",
  seats: DEFAULT_SEATS,
  premiumFlags: [...DEFAULT_PREMIUM_FLAGS],
  stripeCustomerRef: null,
  updatedAt: null,
  updatedBy: null,
});

/** Seats left, never below zero. */
export function seatsRemainingOf(seats: number, inUse: number): number {
  return Math.max(0, seats - inUse);
}
